import { useLingui } from '@lingui/react/macro'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { sessionRecordingControl } from 'features/analytics/sessionRecordingControl'
import { MANAGE_USER_PERMISSIONS_PERMISSIONS_LIST } from 'features/manageUsers'
import { DropdownLink } from 'features/navbar/NavbarDropdown'
import { PermissionsGate } from 'features/permissions'
import {
  SEARCH_PARAM_ORG_ID,
  SEARCH_PARAM_SITE_ID,
} from 'routes/(global_search_params)/-searchParams'
import { createUsersSearch } from 'routes/users/-searchParams'
import styled from 'styled-components'
import {
  PERMISSION_GECKO_COST_METRICS_VIEW,
  PERMISSION_GECKO_INTERNAL_MEMBER,
} from 'types'

import {
  type ConfigSettingsProps,
  type ConfigSettingsState,
} from './useConfigSettingsState'

const DropdownToggleButton = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.25rem 1rem;
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  color: ${({ theme }) => theme.forecast.secondaryText};

  &:hover {
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

export function ConfigSettingsTopBarMenuItems({
  orgID,
  siteID,
  state,
}: ConfigSettingsProps & { state: ConfigSettingsState }) {
  const { t } = useLingui()
  return (
    <>
      <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
        <DropdownLink to='/app-config'>{t`Org & Site App Config`}</DropdownLink>
      </PermissionsGate>

      {orgID && (
        <PermissionsGate requires={MANAGE_USER_PERMISSIONS_PERMISSIONS_LIST}>
          <DropdownLink
            to='/users'
            search={createUsersSearch({ orgId: orgID, siteId: siteID })}>
            {t`Manage Users`}
          </DropdownLink>
        </PermissionsGate>
      )}

      <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
        <DropdownLink to='/admin/users'>{t`Manage Permissions`}</DropdownLink>
      </PermissionsGate>

      {state.isSessionRecordingOrg && (
        <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
          <DropdownMenu.Item
            asChild
            onSelect={(e) => {
              e.preventDefault()
              state.setRecordingOn(sessionRecordingControl.toggle())
            }}>
            <DropdownToggleButton>
              {state.recordingOn
                ? t`Session recording: On`
                : t`Session recording: Off (this browser)`}
            </DropdownToggleButton>
          </DropdownMenu.Item>
        </PermissionsGate>
      )}

      <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
        <DropdownLink to='/health-check'>{t`Health Check`}</DropdownLink>
      </PermissionsGate>

      <PermissionsGate requires={[PERMISSION_GECKO_COST_METRICS_VIEW]}>
        <DropdownLink to='/cost-dashboard'>{t`Cost Dashboard`}</DropdownLink>
      </PermissionsGate>

      {orgID && (
        <PermissionsGate requires={[PERMISSION_GECKO_INTERNAL_MEMBER]}>
          <DropdownLink
            to={'/extensions'}
            search={(prev) => ({
              ...prev,
              [SEARCH_PARAM_ORG_ID]: orgID,
              [SEARCH_PARAM_SITE_ID]: siteID,
            })}>
            {t`Manage Extensions`}
          </DropdownLink>
        </PermissionsGate>
      )}
    </>
  )
}
